import React, { useEffect, useRef, useState } from "react";
import styles from "./TileViewTable.module.scss";
import { Pagination } from "antd";
import TileViewCard from "./TileViewCard";
import {
  downloadModalConst,
  getFallBack,
  PAGE_SIZE_OPTIONS,
  SEARCH_FIELD_NAME
} from "./constants";
import { Modal } from "antd";
import { CommonButtonTexts } from "../../../enums/ButtonAttributesEnums";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import SocketLoader from "../../../reusable-components/SocketLoader";
import { socketActionType } from "../../../enums/socketEnums";
import InvoiceManagementServices from "../../../services/invoiceManagement.service";
import { useFormContext } from "react-hook-form";
import TNLoader from "../../../reusable-components/TNLoader";
import JobDatabaseService from "../../../services/jobDatabase.service";
import { storageKeys } from "../../../constants/storageKeys";
import { ApiResponse } from "../../../enums/ApiResponse";

interface TileViewTableProps {
  searchValue: searchValue;
  dateRange?: object;
  handleTileSelect: (startDate: string, endDate: string) => void;
  editAccess: boolean;
  defaultPagination: object,
  pendingDefaults?: boolean,
}

const TileViewTable = ({
  searchValue,
  dateRange,
  handleTileSelect,
  editAccess,
  defaultPagination,
  pendingDefaults
}: TileViewTableProps) => {
  const { getValues } = useFormContext();

  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const { FetchTileViewList, GetTileDownload } = InvoiceManagementServices();
  const { mutateAsync: tileDownloadAsync } = GetTileDownload();
  const { data: defaultData, isFetching: gettingDefaults } = GetPaginationDefaultMeta();

  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [tileViewParams, setTileViewParams] = useState<TileListParamsType>({
    page: 1,
    perPage: Number(sessionStorage.getItem(storageKeys.INVOICE_TILE_PAGESIZE) ?? 10),
  });
  const [isDownloadConfirmOpen, setIsDownloadConfirmOpen] =
    useState<boolean>(false);
  const [initiateDownload, setInitiateDownload] = useState<boolean>(false);
  const [tileDownloadParams, setTileDownloadParams] = useState({
    startDate: "",
    endDate: "",
    search: "",
  });
  const [allInvoicesReviewed, setAllInvoicesReviewed] =
    useState<boolean>(false);

  const {
    data: tileListData,
    isFetching: TileViewFetching,
    isPending: TileViewPending,
    isLoading: TileViewLoading,
    isSuccess: listFetchSuccess,
    refetch,
  } = FetchTileViewList({
    ...tileViewParams,
    startDate: dateRange?.startDate ?? "",
    endDate: dateRange?.endDate ?? "",
    search: searchValue,
  }, pageSizeChanged, !!sessionStorage.getItem(storageKeys.INVOICE_TILE_PAGESIZE));

  const handlePageChange = (page: number) => {
    setTileViewParams((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handlePageSizeChange = (current: number, pageSize: number) => {
    setTileViewParams((prev) => ({
      ...prev,
      page: 1,
      perPage: pageSize,
    }));
    setPageSizeChanged(true);
    try {
      sessionStorage.setItem(storageKeys.INVOICE_TILE_PAGESIZE, pageSize);
    } catch (err) {
      console.error("Err",err)
    }
  };

  const handleActionCall = async () => {
    const response = await tileDownloadAsync(tileDownloadParams, {
      onSuccess: (Response) => {
        return Response;
      },
      onError: (Error) => {
        return Error;
      },
    });
    return response;
  };

  const handleOpenDownloadConfirmOpen = () => {
    setIsDownloadConfirmOpen(true);
  };

  const handleOpenDownloadConfirmClose = () => {
    setIsDownloadConfirmOpen(false);
  };

  const handleInitiateDownloadStart = () => {
    setInitiateDownload(true);
  };
  const handleInitiateDownloadEnd = () => {
    setInitiateDownload(false);
  };

  const handleDownloadClick = (listItem) => {
    setTileDownloadParams({
      search: getValues(SEARCH_FIELD_NAME),
      startDate: listItem?.startDate,
      endDate: listItem?.endDate,
    });
    if (listItem?.meta?.hasUnreviewedRecords) handleOpenDownloadConfirmOpen();
    else handleInitiateDownloadStart();
  };

  const handleDownloadAction = () => {
    handleInitiateDownloadStart();
    handleOpenDownloadConfirmClose();
  };

  useEffect(() => {
    if(
      (defaultData?.message && defaultData?.message === ApiResponse.SUCCESS)
    ) {
      setTileViewParams((prev) => ({
        ...prev,
        perPage: defaultData?.data?.invoiceTileDefaultPageSize,
      }));
    }
  }, [defaultData])

  useEffect(() => {
    if(listFetchSuccess && pageSizeChanged) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, listFetchSuccess]);

  return (
    <>
      <TNLoader
        isLoading={TileViewPending || TileViewLoading || TileViewFetching}
      />
      <Modal
        visible={isDownloadConfirmOpen}
        onCancel={handleOpenDownloadConfirmClose}
        onOk={handleDownloadAction}
        destroyOnClose
        cancelText={downloadModalConst.CANCEL_AND_REVIEW}
        okText={CommonButtonTexts.CONFIRM}
        width={downloadModalConst.WIDTH}
        title={downloadModalConst.DOWNLOAD_CONFIRMATION_TITLE}
      >
        {downloadModalConst.DOWNLOAD_CONFIRMATION_MSG}
      </Modal>
      <ConditionalRender visible={initiateDownload}>
        <SocketLoader
          actionType={socketActionType.DOWNLOAD_INVOICE}
          handleActionCall={handleActionCall}
          closeRender={handleInitiateDownloadEnd}
        />
      </ConditionalRender>
      <ConditionalRender
        visible={!!tileListData?.data?.length}
        fallback={
          <div className={styles.emptyTable}>
            {getFallBack(
              TileViewPending 
              || TileViewLoading 
              || TileViewFetching,
              searchValue === undefined
            )}
          </div>
        }
      >
        <div className={styles.viewCardsContainer}>
          {Boolean(tileListData?.data?.length) &&
            tileListData?.data?.map((listItem) => {
              return (
                <TileViewCard
                  record={listItem}
                  onTileClick={handleTileSelect}
                  onDownloadClick={handleDownloadClick}
                  editAccess={editAccess}
                />
              );
            })}
        </div>
        <div className={styles.paginationContainer}>
          {tileListData?.meta?.totalRecords && (
            <Pagination
              showTotal={(total, range) => (
                <div>{`${range[0]} - ${range[1]} of ${total} items`}</div>
              )}
              total={tileListData?.meta?.totalRecords}
              current={tileViewParams?.page}
              pageSize={tileViewParams?.perPage ?? 10}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showSizeChanger
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </ConditionalRender>      
    </>
  );
};

export default TileViewTable;
